import dt from 'dayjs'
import * as lib from '../lib'
import {
	IFetchItems,
	IFetchRevenueSpendByAccount,
	TRevenueSpendByAccount,
	TFetchRevenueSpendAgg,
	TRevenueSpendAgg,
	TFetchRevenueAgg,
	TRevenueAgg,
	TFetchOfferSpendAgg,
	TOfferSpendAgg
} from '../types'
import { TIMEOUT_FETCH_REPORT } from '../constants'

export const convertFetchDataRevenueAgg = (
	revenueAgg: TFetchRevenueAgg[]
) => {
	return revenueAgg?.map((item: TFetchRevenueAgg): TRevenueAgg => {
		const { at, saleAt, conversionAt, clickAt, createdAt, updatedAt, deletedAt, offer, scope, ...row } = item

		return {
			...row,
			at: dt.utc(at).toDate(),
			saleAt: saleAt ? dt(saleAt).toDate() : null,
			conversionAt: dt.utc(conversionAt).toDate(),
			clickAt: dt.utc(clickAt).toDate(),
			createdAt: dt.utc(createdAt).toDate(),
			updatedAt: dt.utc(updatedAt).toDate(),
			deletedAt: deletedAt ? dt(deletedAt).toDate() : null,
			...(!!offer && {
				offer: {
					...offer,
					createdAt: dt(offer.createdAt).toDate(),
					updatedAt: dt(offer.updatedAt).toDate(),
					deletedAt: offer.deletedAt ? dt(offer.deletedAt).toDate() : null,
					scope: !offer?.scope ? null : {
						...offer.scope,
						createdAt: dt(offer.scope.createdAt).toDate(),
						updatedAt: dt(offer.scope.updatedAt).toDate()
					}
				}
			}),
			...(!!scope && {
				scope: {
					...scope,
					createdAt: dt.utc(scope.createdAt).toDate(),
					updatedAt: dt.utc(scope.updatedAt).toDate()
				}
			})
		}
	}) ?? []
}

export const convertFetchDataOfferSpendAgg = (
	offerSpendAgg: TFetchOfferSpendAgg[]
) => {
	return offerSpendAgg?.map((item: TFetchOfferSpendAgg): TOfferSpendAgg => {
		const { at, createdAt, updatedAt, ...row } = item

		return {
			...row,
			at: dt.utc(at).utc().toDate(),
			createdAt: dt(createdAt).toDate(),
			updatedAt: dt(updatedAt).toDate(),
			offer: !row?.offer ? null : {
				...row.offer,
				createdAt: dt(row.offer.createdAt).toDate(),
				updatedAt: dt(row.offer.updatedAt).toDate(),
				deletedAt: row.offer.deletedAt ? dt(row.offer.deletedAt).toDate() : null,
				scope: !row.offer?.scope ? null : {
					...row.offer.scope,
					createdAt: dt(row.offer.scope.createdAt).toDate(),
					updatedAt: dt(row.offer.scope.updatedAt).toDate()
				}
			},
			account: !row?.account ? null : {
				...row.account,
				registeredAt: !!row.account.registeredAt ? dt(row.account.registeredAt).toDate() : null,
				createdAt: dt(row.account.createdAt).toDate(),
				updatedAt: dt(row.account.updatedAt).toDate(),
				deletedAt: !!row.account.deletedAt ? dt(row.account.deletedAt).toDate() : null,
				role: !row.account?.role ? null : {
					...row.account.role,
					createdAt: dt(row.account.role.createdAt).toDate(),
					updatedAt: dt(row.account.role.updatedAt).toDate()
				},
				auth: !row.account?.auth ? null : {
					...row.account.auth,
					updatedAt: dt(row.account.auth.updatedAt).toDate()
				}
			},
			scope: !row?.scope ? null : {
				...row.scope,
				createdAt: dt(row.scope.createdAt).toDate(),
				updatedAt: dt(row.scope.updatedAt).toDate()
			}
		}
	}) ?? []
}

export const listRevenueSpendByAccount = async (
	filter: ReturnType<typeof lib.filterSetup>,
	sort: ReturnType<typeof lib.sortSetup>,
	skip: ReturnType<typeof lib.skipSetup>,
	take: ReturnType<typeof lib.takeSetup>,
	signal: AbortSignal,
	token?: string | null
): Promise<IFetchItems<TRevenueSpendByAccount>> => {
	const headers = lib.bearer(token)
	const method: string = 'GET'
	const params: lib.FetchOptions['params'] = {
		f: filter,
		s: sort,
		skip,
		take
	}

	const uri: string = 'revenue/spend/by/account'

	const options: lib.FetchOptions = {
		headers,
		method,
		params,
		signal,
		timeout: TIMEOUT_FETCH_REPORT
	}

	const data = await lib.fetching<IFetchItems<IFetchRevenueSpendByAccount>>(uri, options)

	const rows = data?.items
		?.map((item: IFetchRevenueSpendByAccount): TRevenueSpendByAccount => {
			const {
				auth,
				role,
				registeredAt,
				createdAt,
				updatedAt,
				deletedAt,
				at,
				spend,
				offerSpend,
				revenue,
				revenueCpa,
				revenueSpend,
				...row
			} = item
			return {
				...row,
				registeredAt: !!registeredAt ? dt.utc(registeredAt).toDate() : null,
				createdAt: dt.utc(createdAt).toDate(),
				updatedAt: dt.utc(updatedAt).toDate(),
				deletedAt: !!deletedAt ? dt.utc(deletedAt).toDate() : null,
				role: !role ? null : {
					...role,
					createdAt: dt.utc(role.createdAt).toDate(),
					updatedAt: dt.utc(role.updatedAt).toDate()
				},
				auth: !auth ? null : {
					...auth,
					updatedAt: dt.utc(auth.updatedAt).toDate()
				},
				at: dt(at).toDate(),
				spend: spend?.map((item: TFetchRevenueSpendAgg): TRevenueSpendAgg => {
					const { at, createdAt, updatedAt, account, agency, ...row } = item

					return {
						...row,
						at: dt(at).toDate(),
						createdAt: dt(createdAt).toDate(),
						updatedAt: dt(updatedAt).toDate(),
						...(!!account && {
							account: {
								...account,
								registeredAt: !!account.registeredAt ? dt.utc(account.registeredAt).toDate() : null,
								createdAt: dt.utc(account.createdAt).toDate(),
								updatedAt: dt.utc(account.updatedAt).toDate(),
								deletedAt: !!account.deletedAt ? dt.utc(account.deletedAt).toDate() : null,
								role: !account?.role ? null : {
									...account.role,
									createdAt: dt.utc(account.role.createdAt).toDate(),
									updatedAt: dt.utc(account.role.updatedAt).toDate()
								},
								auth: !account?.auth ? null : {
									...account.auth,
									updatedAt: dt.utc(account.auth.updatedAt).toDate()
								}
							}
						}),
						...(!!agency && {
							agency: {
								...agency,
								createdAt: dt.utc(agency.createdAt).toDate(),
								updatedAt: dt.utc(agency.updatedAt).toDate(),
								percents: agency.percents?.map(item => ({
									...item,
									at: dt(item.at).toDate(),
									nextAt: item.nextAt ? dt(item.nextAt).toDate() : null
								})) ?? []
							}
						})
					}
				}) ?? [],
				offerSpend: convertFetchDataOfferSpendAgg(offerSpend),
				revenue: convertFetchDataRevenueAgg(revenue),
				revenueCpa: convertFetchDataRevenueAgg(revenueCpa),
				revenueSpend: convertFetchDataRevenueAgg(revenueSpend)
			}
		})

	return {
		items: rows ?? [],
		total: data?.total ?? 0
	}
}
